'use client';

import { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/lib/supabase';

interface FormData {
  email: string;
  password: string;
}

// Component that handles search params - needs to be wrapped in Suspense
function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useLanguage();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check for success message in URL params
    const message = searchParams.get('message');
    if (message) {
      setSuccessMessage(message);
    }
    
    // Check for error message in URL params
    const errorMessage = searchParams.get('error');
    if (errorMessage) {
      setError(errorMessage);
    }
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;

      // Fetch user profile to determine redirect destination
      if (data.user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('is_merchant')
          .eq('user', data.user.id)
          .single();

        if (profileError) {
          if (profileError.code === 'PGRST116') {
            // Profile doesn't exist - this shouldn't happen with properly registered users
            console.error('User logged in but no profile found:', data.user.id);
            setError('Account setup incomplete. Please contact support.');
            await supabase.auth.signOut();
            return;
          } else {
            console.error('Error fetching profile:', profileError);
            // Default to home page if profile fetch fails for other reasons
            router.push('/');
            return;
          }
        }

        // Redirect to dashboard if user is a seller, otherwise to home page
        router.push(profile.is_merchant ? '/dashboard' : '/');
      } else {
        // Fallback to home page
        router.push('/');
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An error occurred during sign in',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8'>
      <div className='sm:mx-auto sm:w-full sm:max-w-sm'>
        <h2 className='mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900'>
          {t('auth.login')}
        </h2>
      </div>

      <div className='mt-10 sm:mx-auto sm:w-full sm:max-w-sm'>
        {error && (
          <div className='mb-4 rounded-md bg-red-50 p-4'>
            <div className='flex'>
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-red-800'>{error}</h3>
              </div>
            </div>
          </div>
        )}

        {successMessage && (
          <div className='mb-4 rounded-md bg-green-50 p-4'>
            <div className='flex'>
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-green-800'>
                  {successMessage}
                </h3>
              </div>
            </div>
          </div>
        )}

        <form className='space-y-6' onSubmit={handleSubmit}>
          <div>
            <label
              htmlFor='email'
              className='block text-sm font-medium leading-6 text-gray-900'
            >
              {t('auth.email')}
            </label>
            <div className='mt-2'>
              <input
                id='email'
                name='email'
                type='email'
                autoComplete='email'
                required
                value={formData.email}
                onChange={handleInputChange}
                className='block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6'
              />
            </div>
          </div>

          <div>
            <div className='flex items-center justify-between'>
              <label
                htmlFor='password'
                className='block text-sm font-medium leading-6 text-gray-900'
              >
                {t('auth.password')}
              </label>
              <div className='text-sm'>
                <Link
                  href='/forgot-password'
                  className='font-semibold text-blue-600 hover:text-blue-500'
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </div>
            <div className='mt-2'>
              <input
                id='password'
                name='password'
                type='password'
                autoComplete='current-password'
                required
                value={formData.password}
                onChange={handleInputChange}
                className='block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6'
              />
            </div>
          </div>

          <div className='flex items-center justify-between'>
            <div className='flex items-center'>
              <input
                id='remember-me'
                name='remember-me'
                type='checkbox'
                className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600'
              />
              <label
                htmlFor='remember-me'
                className='ml-3 block text-sm leading-6 text-gray-900'
              >
                {t('auth.rememberMe')}
              </label>
            </div>
          </div>

          <div>
            <button
              type='submit'
              disabled={loading}
              className='flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-70'
            >
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </button>
          </div>
        </form>

        <p className='mt-10 text-center text-sm text-gray-500'>
          {t('auth.notMember')}{' '}
          <Link
            href='/register'
            className='font-semibold leading-6 text-blue-600 hover:text-blue-500'
          >
            {t('auth.registerAccount')}
          </Link>
        </p>
      </div>
    </div>
  );
}

// Main component that wraps LoginForm in Suspense
export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className='flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8'>
          <div className='sm:mx-auto sm:w-full sm:max-w-sm'>
            <div className='animate-pulse'>
              <div className='h-8 bg-gray-200 rounded w-32 mx-auto'></div>
            </div>
          </div>
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
